import { AuthenticationScheme } from '@see-power-pft-common-js/auth/enums';
import { AuthStoreAccess } from 'common-react/auth/auth-store';
import { AuthActionHooks, IAuthScheme } from 'common-react/auth/AuthProvider';
import {
  getCipm3SchemeAuthenticationJwtTokenUsingAuthorizationCode,
  getCipm3SchemeAuthenticationJwtTokenUsingRefreshToken,
  redirectToCipm3Login,
  redirectToCipm3Logout,
} from 'common-react/auth/schemes/cipm3/cipm3';
import { useCallback, useEffect, useMemo } from 'react';

const getIsCipm3Enabled = () => {
  return import.meta.env.VITE_CIPM3_ENABLED === 'true';
};

const tryHandleRedirectState = (redirectState: string | null) => {
  if (redirectState && redirectState !== import.meta.env.VITE_CIPM3_STATE) {
    // we have a "go back to" state=/1/optimisation for example

    // Solve for both scheme possibilities
    const scheme = `${import.meta.env.VITE_CIPM3_REDIRECT_URI}`.startsWith(
      'https://',
    )
      ? 'https://'
      : 'http://';

    const cipmRedirectUriWithoutScheme = `${
      import.meta.env.VITE_CIPM3_REDIRECT_URI
    }`
      .replace('https://', '')
      .replace('http://', '');

    // cipmRedirectUri may or may not end in a /
    // redirectState may or may not begin with a /
    // result should be a valid URI of the form cipmRedirectUri/redirectState
    const href = `${scheme}${`${cipmRedirectUriWithoutScheme}/${redirectState}`
      .replace('//', '/')
      .replace('//', '/')}`;

    window.location.href = `${href}?state=${import.meta.env.VITE_CIPM3_STATE}`;

    return true;
  }

  return false;
};

const doCipmLogin = async (
  authStoreAccess: AuthStoreAccess,
  onStartLogin: AuthActionHooks['onStartLogin'],
) => {
  if (authStoreAccess.getAuthenticationScheme()) {
    return;
  }

  onStartLogin();

  authStoreAccess.resetForAuthenticationScheme(AuthenticationScheme.CIPM3);

  redirectToCipm3Login();
};

const usedAuthorizationCodes = new Set<string>();

const useCipmCallbackHandling = (props: {
  authStoreAccess: AuthStoreAccess;
  actionHooks: AuthActionHooks;
}) => {
  useEffect(() => {
    void (async () => {
      const searchParams = new URLSearchParams(window.location.search);

      if (
        props.authStoreAccess.getAuthenticationScheme() !==
        AuthenticationScheme.CIPM3
      ) {
        return;
      }

      const authorizationCode = searchParams.get('code');

      if (authorizationCode) {
        if (usedAuthorizationCodes.has(authorizationCode)) {
          // prevent dev-mode double-callback issue from causing a second login attempt
          // with the same authorization code, which will fail, and cause an immediate log-out
          return;
        }

        usedAuthorizationCodes.add(authorizationCode);

        const newJwtToken =
          await getCipm3SchemeAuthenticationJwtTokenUsingAuthorizationCode(
            authorizationCode,
          );

        if (newJwtToken) {
          props.authStoreAccess.setJwtToken(newJwtToken);

          props.actionHooks.onEndLogin();

          return;
        }

        // eslint-disable-next-line no-console
        console.error(
          'Handling CIPM3 redirect but failed to get JWT token using authorization code',
        );

        props.actionHooks.onStartLogout();

        props.authStoreAccess.clearAuthState();

        props.actionHooks.onEndLogout();

        return;
      }

      const redirectState = searchParams.get('state');

      if (tryHandleRedirectState(redirectState)) {
        return;
      }

      const isLogout = searchParams.get('logout') === 'true';

      if (!redirectState) {
        if (!isLogout && props.authStoreAccess.getJwtToken()) {
          return;
        }

        props.actionHooks.onStartLogout();

        props.authStoreAccess.clearAuthState();

        props.actionHooks.onEndLogout();

        // eslint-disable-next-line no-console
        console.error('Handling CIPM3 redirect but state missing');

        return;
      }

      if (redirectState === import.meta.env.VITE_CIPM3_STATE) {
        // redirected back from CIPM screens
        return;
      }

      // redirect processing failed
      props.actionHooks.onStartLogout();

      props.authStoreAccess.clearAuthState();

      props.actionHooks.onEndLogout();
    })();
  }, [props]);
};

export const useCipm3AuthScheme = (props: {
  authStoreAccess: AuthStoreAccess;
  actionHooks: AuthActionHooks;
}): IAuthScheme => {
  const isEnabled = useMemo(() => getIsCipm3Enabled(), []);

  const login = useCallback(async () => {
    await doCipmLogin(props.authStoreAccess, props.actionHooks.onStartLogin);
  }, [props]);

  const logout = useCallback(async () => {
    if (!getIsCipm3Enabled()) {
      return;
    }

    if (
      props.authStoreAccess.getAuthenticationScheme() !==
      AuthenticationScheme.CIPM3
    ) {
      return;
    }

    // no possibility to register end of logout in callbacks
    props.actionHooks.onEndLogout();

    redirectToCipm3Logout();
  }, [props]);

  const refresh = useCallback(async () => {
    if (!getIsCipm3Enabled()) {
      return;
    }

    // use getAuthenticationScheme() to avoid rerender
    if (
      props.authStoreAccess.getAuthenticationScheme() !==
      AuthenticationScheme.CIPM3
    ) {
      return;
    }

    const jwtToken = props.authStoreAccess.getJwtToken();
    if (!jwtToken) {
      return;
    }

    props.actionHooks.onStartLogin();

    const payload = atob(jwtToken.split('.')[1]);

    const {
      CIPM: { refreshToken },
    } = JSON.parse(payload) as { CIPM: { refreshToken: string } };

    const newJwtToken =
      await getCipm3SchemeAuthenticationJwtTokenUsingRefreshToken(refreshToken);
    if (newJwtToken) {
      props.authStoreAccess.setJwtToken(newJwtToken);

      props.actionHooks.onEndLogin();

      return;
    }

    props.actionHooks.onStartLogout();

    props.authStoreAccess.clearAuthState();

    props.actionHooks.onEndLogout();
  }, [props]);

  useCipmCallbackHandling(props);

  return useMemo(
    () => ({ isEnabled, login, logout, refresh }),
    [isEnabled, login, logout, refresh],
  );
};
